import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { fadeUp, stagger, viewportOnce, EASE } from "@/lib/motion";
import { AUS_STATES, AUS_CITIES, AUS_VIEWBOX } from "@/lib/australia-map";
import WaitlistDialog from "@/components/WaitlistDialog";
import {
  trackMapStateClick,
  trackMapStateHover,
  trackWaitlistOpen,
} from "@/lib/analytics";

// Timing for the one-shot city tour that fires the first time the map
// enters the viewport.
const TOUR_STEP_MS = 650;

function ShimmerMap({ hovered, setHovered, onStateClick, tourIndex }) {
  return (
    <svg
      viewBox={AUS_VIEWBOX}
      className="h-full w-full"
      role="img"
      aria-label="Map of Australia showing Sydney, Melbourne, Brisbane and Perth"
    >
      <defs>
        {/* Rich metallic gold — used for the outline */}
        <linearGradient id="aus-gold" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#C9A85F" />
          <stop offset="45%" stopColor="#E6D3A3" />
          <stop offset="70%" stopColor="#C9A85F" />
          <stop offset="100%" stopColor="#B18B3F" />
        </linearGradient>
        {/* Soft warm fill to make the land read against the charcoal */}
        <linearGradient id="aus-fill" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#F7E7CE" stopOpacity="0.32" />
          <stop offset="100%" stopColor="#C9A85F" stopOpacity="0.14" />
        </linearGradient>
        {/* Hovered state — brighter gold so it lifts off the continent */}
        <linearGradient id="aus-fill-hover" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#F7E7CE" stopOpacity="0.65" />
          <stop offset="100%" stopColor="#C9A85F" stopOpacity="0.42" />
        </linearGradient>
        {/* Live state (NSW / Sydney) — subtle permanent glow */}
        <linearGradient id="aus-fill-live" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#F7E7CE" stopOpacity="0.48" />
          <stop offset="100%" stopColor="#C9A85F" stopOpacity="0.24" />
        </linearGradient>
        {/* Glow around pins */}
        <radialGradient id="aus-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#E6D3A3" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#C9A85F" stopOpacity="0" />
        </radialGradient>
        {/* Animated shimmer sweep along the coastline */}
        <linearGradient id="aus-shimmer" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="transparent" />
          <stop offset="45%" stopColor="rgba(255,247,222,0.85)" />
          <stop offset="55%" stopColor="rgba(255,247,222,0.85)" />
          <stop offset="100%" stopColor="transparent" />
          <animate
            attributeName="x1"
            values="-100%;100%"
            dur="6s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="x2"
            values="0%;200%"
            dur="6s"
            repeatCount="indefinite"
          />
        </linearGradient>
      </defs>

      {/* Faint dotted grid for editorial texture */}
      <g opacity="0.14">
        {Array.from({ length: 25 }).map((_, i) => (
          <line
            key={`v${i}`}
            x1={40 * i}
            y1="0"
            x2={40 * i}
            y2="966"
            stroke="#C9A85F"
            strokeWidth="0.5"
            strokeDasharray="2 6"
          />
        ))}
        {Array.from({ length: 25 }).map((_, i) => (
          <line
            key={`h${i}`}
            x1="0"
            y1={40 * i}
            x2="1000"
            y2={40 * i}
            stroke="#C9A85F"
            strokeWidth="0.5"
            strokeDasharray="2 6"
          />
        ))}
      </g>

      {/* Land — soft cream fill, subtle gold state borders. Each state is
          interactive so the surrounding component can show a launch tooltip. */}
      <g>
        {AUS_STATES.map((s) => {
          const isActive = hovered === s.id;
          const isLive = s.status === "live";
          const canClick = Boolean(s.city);
          return (
            <path
              key={`fill-${s.id}`}
              d={s.d}
              data-testid={`aus-state-${s.id.toLowerCase()}`}
              fill={
                isActive
                  ? "url(#aus-fill-hover)"
                  : isLive
                    ? "url(#aus-fill-live)"
                    : "url(#aus-fill)"
              }
              stroke="rgba(201,168,95,0.35)"
              strokeWidth="0.8"
              strokeLinejoin="round"
              onMouseEnter={() => {
                setHovered(s.id);
                trackMapStateHover(s.id);
              }}
              onMouseLeave={() => setHovered((cur) => (cur === s.id ? null : cur))}
              onFocus={() => setHovered(s.id)}
              onBlur={() => setHovered(null)}
              onClick={canClick ? () => onStateClick(s) : undefined}
              onKeyDown={
                canClick
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onStateClick(s);
                      }
                    }
                  : undefined
              }
              tabIndex={0}
              role={canClick ? "button" : undefined}
              aria-label={
                canClick
                  ? `Join the ${s.city} Book Away waitlist`
                  : `${s.name} — ${s.launchLabel}`
              }
              style={{
                cursor: canClick ? "pointer" : "default",
                transition: "fill 240ms ease",
              }}
            >
              <title>{`${s.name} — ${s.launchLabel}`}</title>
            </path>
          );
        })}
      </g>

      {/* Bright gold outline on every state — reads as continent silhouette */}
      <g>
        {AUS_STATES.map((s) => (
          <path
            key={`stroke-${s.id}`}
            d={s.d}
            fill="none"
            stroke="url(#aus-gold)"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        ))}
      </g>

      {/* Animated shimmer sweep across the outline */}
      <g>
        {AUS_STATES.map((s) => (
          <path
            key={`shimmer-${s.id}`}
            d={s.d}
            fill="none"
            stroke="url(#aus-shimmer)"
            strokeWidth="2.4"
            strokeLinejoin="round"
          />
        ))}
      </g>

      {/* City pins */}
      {AUS_CITIES.map((c, i) => {
        const anchor = c.align === "end" ? "end" : "start";
        const labelX = anchor === "end" ? -18 : 18;
        const isTouring = tourIndex === i;
        return (
        <g key={c.name} transform={`translate(${c.x}, ${c.y})`}>
          <circle r="34" fill="url(#aus-glow)" />
          {/* One-shot tour ring — expands + fades when this pin's turn comes up */}
          {isTouring && (
            <circle
              r="6"
              fill="none"
              stroke="url(#aus-gold)"
              strokeWidth="2.4"
              opacity="0"
              data-testid={`tour-ring-${c.name.toLowerCase()}`}
            >
              <animate
                attributeName="r"
                values="6;42"
                dur="700ms"
                begin="0s"
                fill="freeze"
              />
              <animate
                attributeName="opacity"
                values="0;1;0"
                dur="700ms"
                begin="0s"
                fill="freeze"
              />
              <animate
                attributeName="stroke-width"
                values="3;0.5"
                dur="700ms"
                begin="0s"
                fill="freeze"
              />
            </circle>
          )}
          {c.current ? (
            <>
              <circle r="9" fill="url(#aus-gold)" />
              <circle
                r="15"
                fill="none"
                stroke="url(#aus-gold)"
                strokeWidth="1.4"
              />
            </>
          ) : (
            <>
              <circle
                r="8"
                fill="none"
                stroke="url(#aus-gold)"
                strokeWidth="2.2"
              >
                <animate
                  attributeName="r"
                  values="5;13;5"
                  dur="2.8s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="1;0.2;1"
                  dur="2.8s"
                  repeatCount="indefinite"
                />
              </circle>
              <circle r="4" fill="url(#aus-gold)" />
            </>
          )}
          <text
            x={labelX}
            y="6"
            textAnchor={anchor}
            fontFamily="Outfit, sans-serif"
            fontSize="18"
            letterSpacing="2.6"
            fill="#F7F5F2"
            style={{ textTransform: "uppercase" }}
          >
            {c.name.toUpperCase()}
          </text>
          {c.current && (
            <text
              x={labelX}
              y="26"
              textAnchor={anchor}
              fontFamily="Outfit, sans-serif"
              fontSize="11"
              letterSpacing="3"
              fill="#E6D3A3"
              style={{ textTransform: "uppercase" }}
            >
              LIVE
            </text>
          )}
        </g>
        );
      })}
    </svg>
  );
}

export default function Expansion() {
  const upcoming = AUS_CITIES.filter((c) => !c.current);
  const [hovered, setHovered] = useState(null);
  const [tourIndex, setTourIndex] = useState(-1);
  const [waitlistCity, setWaitlistCity] = useState(null);
  const tourFiredRef = useRef(false);
  const mapWrapRef = useRef(null);
  const activeState = hovered ? AUS_STATES.find((s) => s.id === hovered) : null;
  const openWaitlist = (city, source = "expansion_cta") => {
    setWaitlistCity(city);
    trackWaitlistOpen(city, source);
  };

  // One-shot micro-tour: the first time the map scrolls into view, pulse each
  // city pin in sequence so visitors clock all four locations before their eyes
  // wander back to the copy. Second scroll-ins do nothing.
  useEffect(() => {
    const el = mapWrapRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const timeouts = [];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || tourFiredRef.current) return;
          tourFiredRef.current = true;
          observer.disconnect();
          AUS_CITIES.forEach((_, i) => {
            timeouts.push(setTimeout(() => setTourIndex(i), i * TOUR_STEP_MS));
          });
          timeouts.push(
            setTimeout(
              () => setTourIndex(-1),
              AUS_CITIES.length * TOUR_STEP_MS + 200
            )
          );
        });
      },
      { threshold: 0.35 }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      timeouts.forEach(clearTimeout);
    };
  }, []);

  return (
    <section
      id="expansion"
      data-testid="expansion-section"
      className="relative z-10 overflow-hidden bg-[#2A2724] px-6 py-28 text-white md:px-12 md:py-40"
    >
      {/* Soft gold vignettes */}
      <div className="pointer-events-none absolute -left-40 top-0 h-[600px] w-[600px] rounded-full bg-[radial-gradient(circle,rgba(230,211,163,0.15),transparent_70%)]" />
      <div className="pointer-events-none absolute -right-40 bottom-0 h-[600px] w-[600px] rounded-full bg-[radial-gradient(circle,rgba(201,168,95,0.12),transparent_70%)]" />

      <div className="relative mx-auto grid max-w-[1600px] grid-cols-1 items-center gap-14 lg:grid-cols-12 lg:gap-16">
        {/* Text */}
        <motion.div
          variants={stagger(0.1)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="lg:col-span-5"
        >
          <motion.p variants={fadeUp} className="overline mb-6 !text-[#E6D3A3]">
            Expansion
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="font-serif text-5xl font-light leading-[1.02] tracking-tight text-white md:text-6xl lg:text-7xl"
          >
            Expanding across{" "}
            <span className="italic text-gold">Australia</span> soon.
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="mt-8 max-w-lg text-lg leading-relaxed text-[#C7BFB4]"
          >
            Are you a salon owner in{" "}
            <span className="text-white">Melbourne</span>,{" "}
            <span className="text-white">Brisbane</span>, or{" "}
            <span className="text-white">Perth</span>? Lock in a Foundation Member
            spot for your city before we launch — Sydney's twenty are almost gone.
          </motion.p>

          <motion.div
            variants={fadeUp}
            className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap"
          >
            {upcoming.map((c) => (
              <button
                key={c.name}
                type="button"
                data-testid={`expansion-cta-${c.name.toLowerCase()}`}
                onClick={() => openWaitlist(c.name)}
                className="group inline-flex items-center justify-center gap-2 rounded-full border border-gold/60 px-6 py-3.5 text-sm font-medium text-[#F7F5F2] transition-colors duration-300 hover:border-transparent hover:bg-gold hover:text-[#2A2724]"
              >
                Join the {c.name} waitlist
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            ))}
          </motion.div>

          <motion.p variants={fadeUp} className="mt-6 text-xs text-[#8f857a]">
            Leave your details and we'll hold a Foundation Member spot for you
            — or tap a state on the map to do the same.
          </motion.p>
        </motion.div>

        {/* Map */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={viewportOnce}
          transition={{ duration: 1.1, ease: EASE }}
          className="lg:col-span-7"
        >
          <div ref={mapWrapRef} className="relative mx-auto aspect-[1000/966] w-full max-w-[900px]">
            <ShimmerMap
              hovered={hovered}
              setHovered={setHovered}
              onStateClick={(s) => {
                if (!s.city) return;
                trackMapStateClick(s.id, s.city);
                openWaitlist(s.city, "expansion_map");
              }}
              tourIndex={tourIndex}
            />

            {/* Hover tooltip — sits over the map, updates as visitors move
                across states. Anchored top-left so it never overlaps pins. */}
            <div
              data-testid="aus-state-tooltip"
              className={`pointer-events-none absolute left-4 top-4 max-w-[240px] rounded-2xl border border-gold/40 bg-[#2A2724]/85 px-4 py-3 backdrop-blur-md transition-opacity duration-300 ${
                activeState ? "opacity-100" : "opacity-0"
              }`}
            >
              <p className="text-[0.62rem] uppercase tracking-[0.28em] text-[#E6D3A3]">
                {activeState?.status === "live" ? "Live now" : "Coming soon"}
              </p>
              <p className="mt-1 font-serif text-lg leading-tight text-white">
                {activeState?.name ?? ""}
              </p>
              <p className="mt-1 text-xs leading-snug text-[#C7BFB4]">
                {activeState?.launchLabel ?? ""}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      <WaitlistDialog
        open={Boolean(waitlistCity)}
        onOpenChange={(v) => !v && setWaitlistCity(null)}
        city={waitlistCity}
      />
    </section>
  );
}
